import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import CabiMascot from "../../components/Kids/CabiMascot";
import useEntrySound from "../../hooks/useEntrySound";
import { planFeatureList } from "../../utils/kidsPlanFeatures";
import { track } from "../../utils/analytics";
import Seo from "../../components/Seo";
import SEO from "../../seo/pages.json";
import { kidsProductSchema, mobileAppSchema, faqSchema, breadcrumbSchema } from "../../utils/structuredData";
import { KIDS_FAQ } from "../../data/kidsFaq";

const API = process.env.REACT_APP_API_URL;

const SUBJECTS = [
  { icon: "🔢", label: "Math" },
  { icon: "📖", label: "English" },
  { icon: "🔭", label: "Science" },
  { icon: "🎨", label: "Art" },
  { icon: "💻", label: "Coding" },
  { icon: "🌍", label: "Arabic" },
];

const HOW_STEPS = [
  { step: "1", icon: "👨‍👩‍👧", title: "Create your account", desc: "Sign up and add your children's profiles in under two minutes." },
  { step: "2", icon: "🎮", title: "Kids pick an adventure", desc: "Cabi recommends the perfect game for their grade and subject." },
  { step: "3", icon: "📊", title: "You watch them grow", desc: "Weekly reports show wins, streaks and where to focus next." },
];

const CABI_POINTS = [
  "Answers any question — math, science, reading & more",
  "Explains things at exactly your child's level",
  "Celebrates every win, never makes them feel bad",
  "Adapts to how your child learns best",
];

const TESTIMONIALS = [
  { text: "My kids actually ASK to do their learning time now. Never thought I'd see the day!", avatar: "👩", name: "Sarah M.", role: "Mother of 2" },
  { text: "The AI tutor explains things so my Grade 3 daughter finally gets it. Amazing.", avatar: "👨‍💼", name: "Ahmed K.", role: "Father" },
  { text: "The progress reports show me exactly what my son loves and where he needs help.", avatar: "👩‍💻", name: "Priya L.", role: "Parent" },
];

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.cabil.kids";

const APP_SCREENS = [
  { src: "/images/kids-app/welcome.jpg", title: "Welcome", alt: "Cabil Kids app welcome screen with Kid and Parent login options" },
  { src: "/images/kids-app/kid-picker.jpg", title: "Who's playing?", alt: "Cabil Kids profile picker where each child taps their avatar" },
  { src: "/images/kids-app/game-hub.jpg", title: "Game hub", alt: "Cabil Kids game hub with daily challenge, XP and subject filters" },
  { src: "/images/kids-app/parent-login.jpg", title: "Parent login", alt: "Cabil Kids parent sign-in screen" },
];

const GRADES = ["Kindergarten", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6"];
const GUEST_INPUT = "w-full border rounded-xl px-4 py-3 font-semibold text-[15px] outline-none focus:ring-2 focus:ring-[#EE5F2B]/30 focus:border-[#EE5F2B] transition bg-white";

const FF = "'Fredoka',sans-serif"; // display font

export default function KidsLanding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [billing, setBilling] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [guestLoading, setGuestLoading] = useState(false);
  const [promo, setPromo] = useState(null);
  useEntrySound("Welcome to Cabil Kids! Let's learn and play together!");

  useEffect(() => {
    axios.get(`${API}/api/kids/plans`)
      .then((res) => setPlans(res.data?.data || []))
      .catch(() => {})
      .finally(() => setPlansLoading(false));
    // Show one active partner campaign with a video, if any — a plain
    // coupon-only campaign (no video_url) has nothing to display here.
    axios.get(`${API}/api/kids/promo/active`)
      .then((res) => setPromo((res.data?.data || []).find((c) => c.video_url) || null))
      .catch(() => {});
  }, []);

  // "Try it now" — no signup. Plays like a real kid session (same token/
  // storage keys as KidLogin.jsx) so the hub, games, and progress all work
  // unmodified; kids_is_guest just flags the free-games-then-signup-wall
  // gate in KidsGameHub's handlePlay().
  // The parent's details are required before the guest session starts and
  // are kept as a trial lead (see GuestAuthController).
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestForm, setGuestForm] = useState({ parent_name: "", email: "", phone: "", grade: "1" });
  const [guestErrors, setGuestErrors] = useState({});

  const tryAsGuest = async (e) => {
    e.preventDefault();
    setGuestLoading(true);
    setGuestErrors({});
    try {
      const res = await axios.post(`${API}/api/kids/guest/login`, {
        ...guestForm,
        grade: parseInt(guestForm.grade, 10),
      });
      const { token, child } = res.data.data;
      localStorage.setItem("kids_parent_token", token);
      localStorage.setItem("kids_active_mode", "child");
      localStorage.setItem("kids_child", JSON.stringify(child));
      sessionStorage.setItem("childId", child.id);
      sessionStorage.setItem("childName", child.name);
      sessionStorage.setItem("kids_is_guest", "1");
      sessionStorage.setItem("kids_guest_games_played", "0");
      track("generate_lead", { lead_source: "kids_try_it_now", grade: guestForm.grade });
      navigate(`/kids/games?childId=${child.id}`);
    } catch (err) {
      const errors = err.response?.status === 422 ? err.response.data?.errors : null;
      if (errors) {
        setGuestErrors(Object.fromEntries(Object.entries(errors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v])));
      } else {
        toast.error("Couldn't start a trial session. Please try again.");
      }
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <div className="overflow-x-hidden" style={{ fontFamily: "'Nunito',sans-serif", color: "#2A2620", background: "#FFFBF7" }}>
      <Seo
        {...SEO["/kids"]}
        path="/kids"
        jsonLd={[
          kidsProductSchema(),
          mobileAppSchema(),
          faqSchema(KIDS_FAQ),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Cabil Kids", path: "/kids" }]),
        ]}
      />

      {/* NAV */}
      <nav className="sticky top-0 z-40 border-b" style={{ backdropFilter: "blur(12px)", background: "rgba(255,251,247,0.82)", borderColor: "#F2E5DA" }}>
        <div className="max-w-[1120px] mx-auto px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <CabiMascot mood="happy" size={38} />
            <span style={{ fontFamily: FF }} className="font-bold text-[22px]">Cabil <span style={{ color: "#EE5F2B" }}>Kids</span></span>
          </div>
          <div className="flex items-center gap-2.5">
            <Link to="/kids/login" className="font-extrabold text-[15px] px-4 py-2 rounded-xl transition hover:bg-[#F4E9DF]" style={{ color: "#5C5349" }}>Log in</Link>
            <Link to="/kids/signup" className="font-extrabold text-[15px] text-white px-5 py-2.5 rounded-xl transition hover:brightness-95"
              style={{ background: "#EE5F2B", boxShadow: "0 6px 16px rgba(238,95,43,.28)" }}>Start free</Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden"
        style={{ background: "radial-gradient(1100px 500px at 78% -10%,#FFE7D5 0%,rgba(255,231,213,0) 60%),linear-gradient(180deg,#FFFBF7 0%,#FFF4EC 100%)" }}>
        <div className="max-w-[1120px] mx-auto px-5 pt-12 pb-16 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 bg-white font-extrabold text-[13px] px-3.5 py-1.5 rounded-full" style={{ border: "1px solid #F2D9C7", color: "#C8491E", boxShadow: "0 4px 14px rgba(238,95,43,.08)" }}>🛡️ Parent-approved · Ad-free · Safe</span>
            <h1 style={{ fontFamily: FF, fontSize: "clamp(34px,5vw,60px)", lineHeight: 1.04, letterSpacing: "-1px" }} className="font-bold mt-4 mb-4">
              Screen time they beg for. Learning that <span style={{ color: "#EE5F2B" }}>actually sticks.</span>
            </h1>
            <p className="text-[18px] leading-relaxed max-w-[480px] mb-7" style={{ color: "#6E6459" }}>
              Playful games, a patient AI buddy, and weekly progress you can trust — for ages 4 to 12. Set up in under two minutes.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/kids/signup" className="font-extrabold text-[17px] text-white px-7 py-4 rounded-2xl transition hover:-translate-y-0.5"
                style={{ background: "#EE5F2B", boxShadow: "0 12px 26px rgba(238,95,43,.32)" }}>Start 14-day free trial →</Link>
              <button onClick={() => setShowGuestForm(true)}
                className="font-extrabold text-[17px] px-6 py-4 rounded-2xl bg-white transition hover:bg-[#FFF4EC]"
                style={{ color: "#2A2620", border: "1.5px solid #EAD9CB" }}>
                Try it now, no signup
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-6 text-[14px] font-bold" style={{ color: "#8A8175" }}>
              <span>⭐⭐⭐⭐⭐ 4.9/5</span><span className="hidden sm:block w-px h-4" style={{ background: "#E4D6C8" }}></span>
              <span>2,000+ families</span><span className="hidden sm:block w-px h-4" style={{ background: "#E4D6C8" }}></span>
              <span>No card needed</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-5">
              <GooglePlayBadge placement="hero" />
              <span className="text-[13px] font-bold" style={{ color: "#2FB67C" }}>● Now live on Android</span>
            </div>
          </div>

          <div className="relative h-[300px] sm:h-[380px]">
            <div className="absolute inset-0 flex items-center justify-center">
              <div style={{ width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle at 50% 40%,#FFD9BF,#FFB98F)", filter: "blur(2px)" }} className="sm:w-[300px] sm:h-[300px]"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center" style={{ animation: "kfloatY 4s ease-in-out infinite" }}>
              <CabiMascot mood="waving" size={200} className="sm:hidden" />
              <span className="hidden sm:block"><CabiMascot mood="waving" size={240} /></span>
            </div>
            <Chip className="top-[6%] start-[2%]" delay="0s">🔢 Math · +20 XP</Chip>
            <Chip className="bottom-[12%] start-0" delay=".6s">🔥 5-day streak!</Chip>
            <Chip className="top-[18%] end-[2%]" delay=".3s">🏆 Level 4</Chip>
          </div>
        </div>
      </section>

      {/* PARTNER PROMO — only rendered when an active campaign has a video */}
      {promo && (
        <section className="max-w-[1120px] mx-auto px-5 pb-4">
          <div className="rounded-[24px] p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-5"
            style={{ background: "linear-gradient(100deg,#FFF3E9,#FFE7D5)", border: "1px solid #F2D9C7" }}>
            <div className="w-full sm:w-[280px] shrink-0 rounded-2xl overflow-hidden shadow-md" style={{ aspectRatio: "16/9" }}>
              <iframe
                src={promo.video_url}
                title={`${promo.partner_name} recommends Cabil Kids`}
                className="w-full h-full"
                style={{ border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="flex-1 text-center sm:text-start">
              <p className="font-extrabold text-[13px] mb-1" style={{ color: "#C8491E" }}>🎁 {promo.partner_name} recommends Cabil Kids</p>
              <p className="text-[15px] mb-3" style={{ color: "#6E6459" }}>
                Sign up with code <b className="font-mono">{promo.code}</b> and get <b>{promo.reward_days} days free</b> — on top of the usual trial.
              </p>
              <Link to={`/kids/signup?promo=${promo.code}`}
                className="inline-block font-extrabold text-[15px] text-white px-6 py-3 rounded-xl transition hover:-translate-y-0.5"
                style={{ background: "#EE5F2B", boxShadow: "0 8px 18px rgba(238,95,43,.28)" }}>
                Use code {promo.code} →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* MOBILE APP */}
      <section id="app" className="px-5 py-16" style={{ background: "#FFF4EC" }}>
        <div className="max-w-[1120px] mx-auto">
          <div className="text-center mb-10">
            <span className="font-extrabold text-[14px] tracking-wide" style={{ color: "#EE5F2B" }}>NOW ON GOOGLE PLAY</span>
            <h2 style={{ fontFamily: FF, fontSize: "clamp(26px,3.5vw,40px)" }} className="font-bold mt-2 mb-2.5">Take Cabil Kids everywhere</h2>
            <p className="text-[17px] max-w-[560px] mx-auto mb-6" style={{ color: "#6E6459" }}>
              The same games, XP and progress — on your Android phone or tablet. Kids tap their picture, enter their PIN and play.
            </p>
            <div className="flex justify-center"><GooglePlayBadge placement="app_section" /></div>
          </div>
          <div className="flex md:grid md:grid-cols-4 gap-5 overflow-x-auto md:overflow-visible snap-x snap-mandatory pb-3 -mx-5 px-5 md:mx-0 md:px-0">
            {APP_SCREENS.map((s) => (
              <figure key={s.src} className="snap-center shrink-0 w-[200px] md:w-auto m-0">
                <div className="rounded-[30px] p-2 bg-[#2A2620]" style={{ boxShadow: "0 18px 40px rgba(95,70,40,.18)" }}>
                  <img src={s.src} alt={s.alt} loading="lazy" width="720" height="1600"
                    className="block w-full h-auto rounded-[22px]" />
                </div>
                <figcaption className="text-center font-extrabold text-[14px] mt-3" style={{ color: "#5C5349" }}>{s.title}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* TWO WORLDS */}
      <section className="max-w-[1120px] mx-auto px-5 py-16">
        <div className="text-center mb-10">
          <h2 style={{ fontFamily: FF, fontSize: "clamp(26px,3.5vw,40px)" }} className="font-bold mb-2.5">One app. Two happy people.</h2>
          <p className="text-[17px]" style={{ color: "#6E6459" }}>A calm command centre for you. A joyful playground for them.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-[26px] p-7" style={{ border: "1px solid #F0E3D6", boxShadow: "0 14px 40px rgba(95,70,40,.06)" }}>
            <span className="font-extrabold text-[13px] tracking-wide" style={{ color: "#8A8175" }}>FOR PARENTS</span>
            <h3 style={{ fontFamily: FF }} className="font-semibold text-[24px] mt-1.5 mb-4">See everything, worry less</h3>
            <div className="flex flex-col gap-2.5">
              <WorldRow bg="#FBF6F0" icon="📊" title="Weekly progress reports" sub="Wins, streaks & what to focus on next" />
              <WorldRow bg="#FBF6F0" icon="⏱️" title="Healthy screen-time limits" sub="You set the daily playtime, we keep it" />
              <WorldRow bg="#FBF6F0" icon="🛡️" title="100% safe & ad-free" sub="No ads, no chat with strangers, ever" />
            </div>
          </div>
          <div className="rounded-[26px] p-7 text-white relative overflow-hidden" style={{ background: "linear-gradient(155deg,#FF8A4A,#EE5F2B 60%,#D94E1F)", boxShadow: "0 18px 44px rgba(238,95,43,.3)" }}>
            <span className="font-extrabold text-[13px] tracking-wide" style={{ color: "#FFE2CF" }}>FOR KIDS</span>
            <h3 style={{ fontFamily: FF }} className="font-semibold text-[24px] mt-1.5 mb-4">An adventure, not a worksheet</h3>
            <div className="flex flex-col gap-2.5">
              <WorldRow light icon="🗺️" title="A learning quest map" sub="Unlock new games as they level up" />
              <WorldRow light icon="⭐" title="XP, coins & daily streaks" sub="Every win is celebrated with Cabi" />
              <WorldRow light icon="🤖" title="Cabi, the AI buddy" sub="Answers any question, never judges" />
            </div>
          </div>
        </div>
      </section>

      {/* SUBJECTS */}
      <section className="px-5 py-16" style={{ background: "#FFF4EC" }}>
        <div className="max-w-[1000px] mx-auto text-center">
          <h2 style={{ fontFamily: FF, fontSize: "clamp(24px,3vw,36px)" }} className="font-bold mb-2">Every subject, made playful</h2>
          <p className="text-[16px] mb-9" style={{ color: "#6E6459" }}>Curriculum-aligned games for Kindergarten to Grade 6.</p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3.5">
            {SUBJECTS.map((s) => (
              <div key={s.label} className="bg-white rounded-[20px] py-5 px-2.5 transition hover:-translate-y-1" style={{ border: "1px solid #F0E3D6", boxShadow: "0 8px 22px rgba(95,70,40,.05)" }}>
                <div className="text-[34px] mb-2">{s.icon}</div>
                <div className="font-extrabold text-[14px]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-[1000px] mx-auto px-5 py-[74px] text-center">
        <span className="font-extrabold text-[14px] tracking-wide" style={{ color: "#EE5F2B" }}>{t("kids.landing.upAndRunning")}</span>
        <h2 style={{ fontFamily: FF, fontSize: "clamp(24px,3vw,36px)" }} className="font-bold mt-2 mb-11">{t("kids.landing.howItWorksTitle")}</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {HOW_STEPS.map((h) => (
            <div key={h.step} className="relative bg-white rounded-[22px] pt-8 pb-7 px-5.5" style={{ border: "1px solid #F0E3D6", boxShadow: "0 10px 30px rgba(95,70,40,.05)", padding: "30px 22px" }}>
              <div className="absolute start-1/2 -translate-x-1/2 -top-4 w-[34px] h-[34px] rounded-full text-white flex items-center justify-center text-[17px]" style={{ background: "#EE5F2B", fontFamily: FF, fontWeight: 700, boxShadow: "0 6px 14px rgba(238,95,43,.3)" }}>{h.step}</div>
              <div className="text-[44px] mt-2.5 mb-3.5">{h.icon}</div>
              <h3 style={{ fontFamily: FF }} className="font-semibold text-[20px] mb-2">{h.title}</h3>
              <p className="text-[15px] leading-relaxed" style={{ color: "#6E6459" }}>{h.desc}</p>
            </div>
          ))}
        </div>
        <Link to="/kids/signup" className="inline-block mt-10 font-extrabold text-[17px] text-white px-8 py-3.5 rounded-2xl transition hover:brightness-95"
          style={{ background: "#EE5F2B", boxShadow: "0 12px 26px rgba(238,95,43,.3)" }}>Create your free account →</Link>
      </section>

      {/* MEET CABI */}
      <section style={{ background: "linear-gradient(180deg,#FFF4EC,#FFFBF7)" }}>
        <div className="max-w-[1040px] mx-auto px-5 py-[70px] grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="font-extrabold text-[14px] tracking-wide" style={{ color: "#EE5F2B" }}>AI LEARNING BUDDY</span>
            <h2 style={{ fontFamily: FF, fontSize: "clamp(28px,3.4vw,40px)" }} className="font-bold mt-2 mb-4">Meet Cabi 🎓</h2>
            <p className="text-[18px] leading-relaxed mb-5" style={{ color: "#6E6459" }}>A patient, encouraging tutor that explains things at exactly your child's level — and cheers every single win.</p>
            <div className="flex flex-col gap-3">
              {CABI_POINTS.map((p) => (
                <div key={p} className="flex gap-2.5 items-start text-[16px] font-semibold" style={{ color: "#3F3930" }}>
                  <span className="font-black" style={{ color: "#2FB67C" }}>✓</span>{p}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-[28px] p-5.5" style={{ boxShadow: "0 22px 50px rgba(95,70,40,.1)", border: "1px solid #F0E3D6", padding: 22 }}>
            <div className="flex items-end gap-2.5 mb-3.5">
              <CabiMascot mood="happy" size={52} />
              <Bubble>Hey Ali! 🌟 What do you want to explore today?</Bubble>
            </div>
            <div className="flex items-end gap-2.5 flex-row-reverse mb-3.5">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-[20px]" style={{ background: "#E7F0FF" }}>🧒</div>
              <div className="rounded-[18px] px-3.5 py-2.5 text-[15px] font-semibold" style={{ background: "#EEF4FF", color: "#3F3930", borderBottomRightRadius: 4 }}>Why is the sky blue?</div>
            </div>
            <div className="flex items-end gap-2.5">
              <CabiMascot mood="celebrating" size={52} />
              <Bubble>Great question! 🎉 Sunlight is made of colours, and our air scatters the blue ones the most — so the sky looks like it's wearing its favourite colour! Want to try a fun science game? 🚀</Bubble>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="max-w-[1040px] mx-auto px-5 py-16 text-center">
        <h2 style={{ fontFamily: FF, fontSize: "clamp(24px,3vw,36px)" }} className="font-bold mb-2">Simple, family-friendly pricing</h2>
        <p className="text-[16px] mb-6" style={{ color: "#6E6459" }}>Every plan starts with a 14-day free trial. Cancel anytime.</p>
        <div className="inline-flex rounded-full p-1.5 mb-9" style={{ background: "#F4E9DF" }}>
          {[["monthly", "Monthly"], ["yearly", "Yearly · save 20%"]].map(([key, label]) => (
            <button key={key} onClick={() => setBilling(key)}
              className="font-extrabold text-[14px] px-4 py-2 rounded-full transition"
              style={billing === key ? { background: "#fff", color: "#2A2620", boxShadow: "0 3px 10px rgba(95,70,40,.1)" } : { color: "#8A8175" }}>
              {label}
            </button>
          ))}
        </div>

        {plansLoading ? (
          <div className="py-12" style={{ color: "#A99B8A" }}>Loading plans…</div>
        ) : plans.length === 0 ? (
          <div className="py-12" style={{ color: "#A99B8A" }}>Plans coming soon.</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5 text-start">
            {plans.map((p, i) => {
              const popular = plans.length === 3 && i === 1;
              const yearly = Number(p.price_yearly) || 0;
              const monthly = Number(p.price_monthly) || 0;
              const perMonth = billing === "yearly" ? (yearly / 12).toFixed(2) : monthly.toFixed(2);
              const features = planFeatureList(p);
              return (
                <div key={p.slug || p.id} className="relative bg-white rounded-[22px] p-6 flex flex-col"
                  style={{ border: popular ? "2px solid #EE5F2B" : "1px solid #F0E3D6", boxShadow: popular ? "0 18px 40px rgba(238,95,43,.16)" : "0 10px 26px rgba(95,70,40,.05)" }}>
                  {popular && (
                    <span className="absolute start-1/2 -translate-x-1/2 -top-3 text-white font-extrabold text-[12px] px-3.5 py-1 rounded-full whitespace-nowrap" style={{ background: "#EE5F2B" }}>{t("kids.landing.mostPopular")}</span>
                  )}
                  <h3 style={{ fontFamily: FF }} className="font-semibold text-[21px] mb-1">{p.name}</h3>
                  <p className="text-[14px] mb-3.5" style={{ color: "#8A8175" }}>Up to {p.max_children} child{p.max_children > 1 ? "ren" : ""}</p>
                  <div className="mb-4">
                    <span style={{ fontFamily: FF }} className="font-bold text-[40px]">PKR {Number(perMonth || 0).toLocaleString()}</span>
                    <span className="text-[15px]" style={{ color: "#8A8175" }}>/mo</span>
                    {billing === "yearly" && <div className="text-[12px] font-bold mt-0.5" style={{ color: "#2FB67C" }}>Billed PKR {Number(yearly || 0).toLocaleString()}/year</div>}
                  </div>
                  <div className="flex flex-col gap-2.5 mb-5.5" style={{ marginBottom: 22 }}>
                    {features.map((f) => (
                      <div key={f} className="flex gap-2.5 items-start text-[14.5px] font-semibold" style={{ color: "#3F3930" }}>
                        <span className="font-black" style={{ color: "#2FB67C" }}>✓</span>{f}
                      </div>
                    ))}
                  </div>
                  <Link to={`/kids/signup?plan=${p.slug}&billing=${billing}`}
                    className="mt-auto text-center font-extrabold text-[15px] py-3 rounded-2xl transition"
                    style={popular
                      ? { background: "#EE5F2B", color: "#fff", boxShadow: "0 10px 22px rgba(238,95,43,.28)" }
                      : { background: "#fff", color: "#EE5F2B", border: "1.5px solid #EE5F2B" }}>
                    Start free trial
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* TESTIMONIALS */}
      <section className="px-5 py-16" style={{ background: "#FFF4EC" }}>
        <div className="max-w-[1040px] mx-auto text-center">
          <h2 style={{ fontFamily: FF, fontSize: "clamp(24px,3vw,36px)" }} className="font-bold mb-10">Parents love it. Kids prove it.</h2>
          <div className="grid md:grid-cols-3 gap-5 text-start">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-white rounded-[22px] p-6" style={{ border: "1px solid #F0E3D6", boxShadow: "0 10px 26px rgba(95,70,40,.05)" }}>
                <div className="text-[15px] mb-2.5" style={{ color: "#EE5F2B" }}>★★★★★</div>
                <p className="text-[15.5px] leading-relaxed italic mb-4" style={{ color: "#4A4339" }}>"{t.text}"</p>
                <div className="flex items-center gap-2.5">
                  <span className="text-[30px]">{t.avatar}</span>
                  <div><div className="font-extrabold text-[14px]">{t.name}</div><div className="text-[12.5px]" style={{ color: "#8A8175" }}>{t.role}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-5 py-16">
        <div className="max-w-[820px] mx-auto">
          <h2 style={{ fontFamily: FF, fontSize: "clamp(24px,3vw,36px)" }} className="font-bold mb-8 text-center">Frequently asked questions</h2>
          <div className="flex flex-col gap-4">
            {KIDS_FAQ.map((f) => (
              <div key={f.q} className="bg-white rounded-[18px] p-5" style={{ border: "1px solid #F0E3D6", boxShadow: "0 8px 22px rgba(95,70,40,.05)" }}>
                <h3 className="font-extrabold text-[16px] mb-1.5">{f.q}</h3>
                <p className="text-[14.5px] leading-relaxed" style={{ color: "#6E6459" }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="text-white text-center px-5 py-[70px] relative overflow-hidden" style={{ background: "linear-gradient(150deg,#FF8A4A,#EE5F2B 55%,#D94E1F)" }}>
        <div className="relative max-w-[640px] mx-auto">
          <div className="flex justify-center mb-2.5"><CabiMascot mood="celebrating" size={110} /></div>
          <h2 style={{ fontFamily: FF, fontSize: "clamp(26px,3.6vw,42px)" }} className="font-bold mb-3">Ready to make learning their favourite part of the day?</h2>
          <p className="text-[18px] mb-7" style={{ color: "#FFE6D7" }}>Join 2,000+ families. Free for 14 days — no card needed.</p>
          <Link to="/kids/signup" className="inline-block font-extrabold text-[18px] px-9 py-4 rounded-[18px] bg-white transition hover:-translate-y-0.5"
            style={{ color: "#EE5F2B", boxShadow: "0 16px 34px rgba(0,0,0,.18)" }}>Start free trial 🚀</Link>
          <div className="flex justify-center mt-5"><GooglePlayBadge placement="footer_cta" /></div>
        </div>
      </section>

      <footer className="text-center px-5 py-7" style={{ background: "#2A2620", color: "#B6AC9F" }}>
        <a href="https://cabil.co" className="inline-flex items-center gap-2 mb-3 px-5 py-2 rounded-full text-white text-sm font-semibold hover:opacity-90 transition"
          style={{ background: "#EE5F2B" }}>
          ← Back to cabil.co
        </a>
        <div className="text-[13.5px] font-semibold">© 2026 Cabil Kids · Safe, ad-free learning for ages 4–12</div>
      </footer>

      {showGuestForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(42,38,32,.55)" }}
          onClick={() => !guestLoading && setShowGuestForm(false)}>
          <form onSubmit={tryAsGuest} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[420px] bg-white rounded-[24px] p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: "0 24px 60px rgba(0,0,0,.25)" }}>
            <div className="flex items-start justify-between gap-3 mb-1">
              <h2 style={{ fontFamily: FF }} className="font-bold text-[24px]">Try Cabil Kids free</h2>
              <button type="button" onClick={() => setShowGuestForm(false)} aria-label="Close"
                className="text-[22px] leading-none px-1" style={{ color: "#8A8175" }}>×</button>
            </div>
            <p className="text-[14.5px] mb-5" style={{ color: "#6E6459" }}>
              A few quick details from a grown-up, then your child can jump straight into the games.
            </p>
            <GuestField label="Parent's name" error={guestErrors.parent_name}>
              <input required maxLength={100} value={guestForm.parent_name} autoComplete="name"
                onChange={(e) => setGuestForm({ ...guestForm, parent_name: e.target.value })}
                className={GUEST_INPUT} style={{ borderColor: "#E5DED4" }} />
            </GuestField>
            <GuestField label="Email" error={guestErrors.email}>
              <input required type="email" maxLength={150} value={guestForm.email} autoComplete="email"
                onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })}
                className={GUEST_INPUT} style={{ borderColor: "#E5DED4" }} />
            </GuestField>
            <GuestField label="Phone (optional)" error={guestErrors.phone}>
              <input type="tel" maxLength={30} value={guestForm.phone} autoComplete="tel"
                onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })}
                className={GUEST_INPUT} style={{ borderColor: "#E5DED4" }} />
            </GuestField>
            <GuestField label="Child's grade" error={guestErrors.grade}>
              <select value={guestForm.grade} onChange={(e) => setGuestForm({ ...guestForm, grade: e.target.value })}
                className={GUEST_INPUT} style={{ borderColor: "#E5DED4" }}>
                {GRADES.map((g, i) => <option key={i} value={i}>{g}</option>)}
              </select>
            </GuestField>
            <button type="submit" disabled={guestLoading}
              className="w-full mt-2 font-extrabold text-[16px] text-white py-3.5 rounded-2xl transition hover:brightness-95 disabled:opacity-60"
              style={{ background: "#EE5F2B", boxShadow: "0 10px 22px rgba(238,95,43,.28)" }}>
              {guestLoading ? "Loading…" : "Start playing →"}
            </button>
            <p className="text-[12px] mt-3 text-center" style={{ color: "#A99B8A" }}>
              We'll only use these details to help you get started with Cabil Kids.{" "}
              <Link to="/kids/privacy" className="underline">Privacy policy</Link>
            </p>
          </form>
        </div>
      )}

      <style>{`
        @keyframes kfloatY{0%,100%{transform:translateY(0)}50%{transform:translateY(-16px)}}
        @keyframes kfloatY2{0%,100%{transform:translateY(0) rotate(-3deg)}50%{transform:translateY(-12px) rotate(3deg)}}
      `}</style>
    </div>
  );
}

function Chip({ children, className = "", delay = "0s" }) {
  return (
    <div className={`absolute bg-white rounded-[18px] px-3.5 py-2.5 font-extrabold text-[13px] sm:text-[14px] ${className}`}
      style={{ boxShadow: "0 12px 30px rgba(0,0,0,.10)", animation: `kfloatY2 5.5s ease-in-out infinite ${delay}` }}>
      {children}
    </div>
  );
}

function GuestField({ label, error, children }) {
  return (
    <label className="block mb-3.5">
      <span className="block font-extrabold text-[13px] mb-1.5" style={{ color: "#2A2620" }}>{label}</span>
      {children}
      {error && <span className="block text-[12.5px] font-semibold mt-1" style={{ color: "#D93A2B" }}>{error}</span>}
    </label>
  );
}

function GooglePlayBadge({ placement = "kids_landing" }) {
  return (
    <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer"
      onClick={() => track("play_store_click", { placement })}
      aria-label="Get Cabil Kids on Google Play"
      className="inline-flex items-center gap-2.5 rounded-xl px-4 py-2 transition hover:-translate-y-0.5"
      style={{ background: "#000", color: "#fff", border: "1px solid #A6A6A6", boxShadow: "0 8px 18px rgba(0,0,0,.18)" }}>
      <svg width="24" height="26" viewBox="0 0 24 26" aria-hidden="true">
        <path d="M1 1.2 13.4 13 1 24.8c-.4-.2-.6-.7-.6-1.2V2.4c0-.5.2-1 .6-1.2Z" fill="#00D7FE" />
        <path d="m17.5 8.9-4.1 4.1L1 1.2c.2-.1.5-.1.8.1l15.7 7.6Z" fill="#00F076" />
        <path d="m17.5 17.1-15.7 7.6c-.3.2-.6.2-.8.1L13.4 13l4.1 4.1Z" fill="#FF3A44" />
        <path d="m22.6 14.3-5.1 2.8-4.1-4.1 4.1-4.1 5.1 2.8c1 .6 1 2 0 2.6Z" fill="#FFD500" />
      </svg>
      <span className="flex flex-col text-start leading-none">
        <span className="text-[10px] font-semibold tracking-wide">GET IT ON</span>
        <span className="text-[18px] font-bold mt-0.5" style={{ fontFamily: "'Nunito',sans-serif" }}>Google Play</span>
      </span>
    </a>
  );
}

function WorldRow({ icon, title, sub, bg, light }) {
  return (
    <div className="flex gap-3 items-center rounded-[14px] px-3.5 py-3" style={{ background: light ? "rgba(255,255,255,.16)" : bg }}>
      <span className="text-[22px]">{icon}</span>
      <div>
        <div className="font-extrabold text-[15px]">{title}</div>
        <div className="text-[13px]" style={{ color: light ? "#FFE2CF" : "#8A8175" }}>{sub}</div>
      </div>
    </div>
  );
}

function Bubble({ children }) {
  return (
    <div className="rounded-[18px] px-3.5 py-2.5 text-[15px] font-semibold" style={{ background: "#FFF1E7", border: "1px solid #FBD9C4", color: "#5C4A3E", borderBottomLeftRadius: 4 }}>
      {children}
    </div>
  );
}
